<template>
  <SfInpageListItem
    :key="item.name"
    :title="item.name"
    :item-id="item.name"
    :compact="true"
    data-testid="sidebar-insights-property"
  >
    <template #subtitles>
      <SfInpageListItemSubtitle>{{ item.value }}</SfInpageListItemSubtitle>
    </template>

    <template #right>
      <div class="tw-flex tw-items-center">
        <span
          v-if="copyToClipboardState === 'initial'"
          v-tooltip.placementLeft="
            I18n.t('sidebar_insights_properties.copy_tooltip')
          "
          data-tooltip-size="xs"
          data-testid="copy-icon"
          @click="copyToClipboard(item)"
        >
          <i :class="copyToClipboardClasses"></i>
        </span>

        <span
          v-if="copyToClipboardState === 'copied'"
          v-tooltip.placementLeft="
            I18n.t('sidebar_insights_properties.copied_tooltip')
          "
          data-tooltip-size="xs"
          data-testid="copied-icon"
          @click="copyToClipboard(item)"
          @mouseleave="resetCopyToClipboardState"
        >
          <i :class="copyToClipboardClasses"></i>
        </span>

        <img
          v-tooltip.placementLeft="
            I18n.t('sidebar_insights_properties.insights_tooltip')
          "
          data-testid="insights-link"
          data-tooltip-size="xs"
          :src="$imagesRequire('icon-live-input-action-insights.svg')"
          :class="iconClasses"
          @click="openInsights(item)"
        />
      </div>
    </template>
  </SfInpageListItem>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'
import type { InsightsProperty } from './types'
import { liveInputEvents } from '../reconciliation/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

interface Props {
  item: InsightsProperty
  originalReconciliationTextId: number
  periodDate: string
}

const props = defineProps<Props>()

const iconClasses = [
  'tw-h-full',
  'hover:tw-bg-sf-neutral-lighter-2',
  'active:tw-bg-sf-neutral-lighter-1',
  'tw-p-2',
  'tw-leading-4',
  'tw-transition-all',
  'tw-rounded-md',
  'fa',
  'tw-text-sf-neutral-light-2',
  'tw-w-[32px]',
  'tw-h-[32px]',
  'tw-text-center',
]
const copyToClipboardState = ref<'initial' | 'copied'>('initial')

const copyToClipboard = (item: InsightsProperty) => {
  const text = `${item.name} | ${item.value} | ${props.originalReconciliationTextId}`
  navigator.clipboard.writeText(text)
  copyToClipboardState.value = 'copied'
}

const copyToClipboardClasses = computed(() => {
  const stateClass =
    copyToClipboardState.value === 'initial'
      ? 'fa-copy'
      : 'fa-check-circle !tw-text-emerald-600'

  return [...iconClasses, stateClass]
})

function resetCopyToClipboardState(): void {
  copyToClipboardState.value = 'initial'
}

function openInsights(item: InsightsProperty): void {
  window.eventHub.$emit(liveInputEvents.INSIGHTS_ACTION_CLICKED, {
    liquidPath: item.name,
    reconciliationTextId: props.originalReconciliationTextId,
    periodDate: props.periodDate,
  })
}
</script>
